/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import {
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';

// Store
import { ACLS } from '@config/constants/acls';
import { ROLES } from '@config/constants/roles';
import { TMeasurabilityProjectQueryParams } from '@features/catalogs/meausrability-projects/list/types';
import {
	TParams,
	TQueryParams,
	TSelectOption,
} from '@global-types/index';
import { useAuthorization } from '@lib/authorization';
import { useElementSize } from '@mantine/hooks';
import useStore from '@store/index';
import {
	cloneDeep,
	removeEmptyArrays,
} from '@utils/functions';
import { TMesurabilityProjectPopulated } from '../types';

/* -------------------------------------------------------------------------- */
/*                                  Hook                                      */
/* -------------------------------------------------------------------------- */

export const useMeasurabilities = () => {
	/**************************** Refs ****************************/
	const containerRef: any = useRef<HTMLElement | null>(
		null
	);

	/**************************** States ****************************/
	const [resetFilterProducers, setResetFilterProducers] =
		useState(false);

	const [
		queryParamsOrganizations,
		setQueryParamsOrganizations,
	] = useState({});

	const [openDrawer, setOpenDrawer] = useState(false);
	const [
		openDrawerAddApiToken,
		setOpenDrawerAddApiToken,
	] = useState(false);

	const [showScrollFilters, setShowScrollFilters] =
		useState(false);
	const [
		openSelectedItemsToDeleteModal,
		setOpenSelectedItemsToDeleteModal,
	] = useState(false);

	const [isOverflowFilters] = useState(false);

	const {
		measurabilitiesSelectedRow,
		measurabilitiesTotalPages,
		measurabilitiesPage,
		measurabilitiesTotal,
		measurabilitiesPageSize,
		setMeasurabilitiesPageSize,
		setMeasurabilitiesPage,
		setMeasurabilitiesSortParams,
		setMeasurabilitiesQueryParams,
		measurabilitiesQueryParams,
		setMeasurabilitiesSortStatus,
		measurabilitiesSortStatus,
		measurabilitiesSortParams,
		setMeasurabilitiesOpenConfirmationModalDelete,
		measurabilitiesOpenConfirmationModalDelete,
		setMeasurabilitiesSelectedRow,
		setAddMeasurabilityProjectFormOpened,
		addMeasurabilityProjectFormOpened,
		setUpdateMeasurabilityProjectFormOpened,
		updateMeasurabilityProjectFormOpened,
		setMeasurabilitiesDatatableOptions,
		measurabilitiesDatatableOptions,
	} = useStore();

	const { ref, width: widthContainer } = useElementSize();
	/**************************** Drawer show more production ****************************/
	const handleAddItem = () => {
		setAddMeasurabilityProjectFormOpened(true);
	};

	const handleDeleteMultipleItems = () => {
		setOpenSelectedItemsToDeleteModal(true);
	};

	/**************************** Reset ****************************/
	const handleReset = () => {
		setMeasurabilitiesPageSize(100);
		setMeasurabilitiesQueryParams({});
		setMeasurabilitiesPage(1);
		setMeasurabilitiesSortParams('-createdAt');
		setMeasurabilitiesSortParams({});
	};

	useEffect(() => {
		return () => {
			handleReset();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const { checkAcl, checkAccess } = useAuthorization();

	useLayoutEffect(() => {
		if (
			containerRef.current &&
			containerRef.current.clientWidth <
				containerRef.current.scrollWidth
		) {
			setShowScrollFilters(true);
		} else {
			setShowScrollFilters(false);
		}
	}, [widthContainer]);

	/**************************** Actions ****************************/
	const handleScroll = (scrollTo: string) => {
		if (containerRef.current) {
			if (scrollTo === 'right') {
				containerRef.current.scrollLeft =
					containerRef.current.scrollLeft +
					containerRef.current.offsetWidth;
			} else {
				containerRef.current.scrollLeft =
					containerRef.current.scrollLeft -
					containerRef.current.offsetWidth;
			}
		}
	};
	const handleSortParams = (_column: any) => {
		let sortParams = _column.columnAccessor;

		switch (_column.columnAccessor) {
			case 'producer':
				sortParams = 'entity';
				break;
			case 'place':
				sortParams = 'location.place';
				break;
			case 'availableStock':
				sortParams = 'quantity.remaining_aco2';
				break;
			case 'sellPrice':
				sortParams = 'sellPrice_europertco2eq';
				break;
			default:
				sortParams;
				break;
		}

		if (_column.direction == 'asc') {
			setMeasurabilitiesSortParams(sortParams);
		} else {
			setMeasurabilitiesSortParams('-' + sortParams);
		}
	};

	/*************************************** Filters  ***************************************/

	const handleActiveFilter = (status: TQueryParams[]) => {
		setMeasurabilitiesPage(1);
		const queryParamsActive = {
			active: status.map(
				(_status: TParams) => _status.value
			),
		};

		setMeasurabilitiesQueryParams({
			...measurabilitiesQueryParams,
			...queryParamsActive,
		});
	};

	const handleOrganizationFilter = (
		organizations: TQueryParams[]
	) => {
		setMeasurabilitiesPage(1);
		const queryParamsOrganizations = {
			organization: organizations.map(
				(_organization: TParams) => _organization.value
			),
			entity: [],
		};

		setMeasurabilitiesQueryParams({
			...measurabilitiesQueryParams,
			...queryParamsOrganizations,
		});
		setQueryParamsOrganizations(queryParamsOrganizations);
		setResetFilterProducers(true);
	};

	const handleProducerFilter = (
		producers: TQueryParams[]
	) => {
		setMeasurabilitiesPage(1);
		const queryParamsProjects = {
			entity: producers.map(
				(_producer: TParams) => _producer.value
			),
		};

		setMeasurabilitiesQueryParams({
			...measurabilitiesQueryParams,
			...queryParamsProjects,
		});
	};

	const handleCountryFilter = (
		_countries: TSelectOption[]
	) => {
		setMeasurabilitiesPage(1);

		const countries = _countries.map(
			(_country: TSelectOption) => _country.value
		);

		if (countries.length == 0) {
			const _offsetMesurabilityProjectsQueryParams =
				cloneDeep(measurabilitiesQueryParams);
			delete _offsetMesurabilityProjectsQueryParams[
				'location.countryCode'
			];
			setMeasurabilitiesQueryParams(
				_offsetMesurabilityProjectsQueryParams
			);
			return;
		}
		const queryParamsCountries = {
			'location.countryCode': {
				$in: countries,
			},
		};
		const filterParams: TMeasurabilityProjectQueryParams =
			removeEmptyArrays({
				...measurabilitiesQueryParams,
				...queryParamsCountries,
			});
		setMeasurabilitiesQueryParams(filterParams);
	};

	const handleTechnologyFilter = (
		_technologies: TSelectOption[]
	) => {
		setMeasurabilitiesPage(1);
		const queryParamsProjects = {
			technology: _technologies.map(
				(_technology: TSelectOption) => _technology.value
			),
		};

		const filterParams: TMeasurabilityProjectQueryParams =
			removeEmptyArrays({
				...measurabilitiesQueryParams,
				...queryParamsProjects,
			});
		setMeasurabilitiesQueryParams(filterParams);
	};

	/*************************************** Delete one Item  ***************************************/

	const handleConfirmDeleteItem = (remove: any) => {
		setMeasurabilitiesOpenConfirmationModalDelete({
			open: false,
			params: {},
		});
		remove.mutate(
			measurabilitiesOpenConfirmationModalDelete.params.id
		);
	};

	const handleCloseConfirmationDeleteItem = () => {
		setMeasurabilitiesOpenConfirmationModalDelete({
			open: false,
			params: {},
		});
	};

	const handleOpenConfirmationDeleteItem = () => {
		/*
		setMeasurabilitiesOpenConfirmationModalDelete({
			open: true,
			params: measurabilitiesSelectedRow,
		});
        */
	};

	/*************************************** Delete multiple Items  ***************************************/

	/*************************************** Datatables  ***************************************/

	const handleViewMore = (
		row: TMesurabilityProjectPopulated
	) => {
		setMeasurabilitiesSelectedRow(row);
		setOpenDrawer(true);
	};

	const handleRowClicked = (row: any) => {
		if (
			checkAcl(ACLS.MEASURABILITIES.READ) &&
			checkAccess({
				allowedRoles: [
					ROLES.ADMIN,
					ROLES.SUPERADMIN,
					ROLES.STAFF,
				],
			})
		) {
			setMeasurabilitiesSelectedRow(row.record);
			setOpenDrawer(true);
		}
	};

	const handleSortStatusChange = (data: any) => {
		handleSortParams(data);
		setMeasurabilitiesSortStatus(data);
	};

	/**************************** Pagination ****************************/

	const handlePaginationChangeSize = function (
		_size: string | number
	) {
		setMeasurabilitiesPageSize(_size);
		setMeasurabilitiesPage(1);
	};

	const handleChangePagination = (data: any) => {
		setMeasurabilitiesPage(data);
	};

	/********************************************************************************************/

	return {
		handlePaginationChangeSize,
		handleChangePagination,
		measurabilitiesTotalPages,
		measurabilitiesPage,
		measurabilitiesTotal,
		measurabilitiesPageSize,
		handleOpenConfirmationDeleteItem,
		handleCloseConfirmationDeleteItem,
		handleConfirmDeleteItem,
		openSelectedItemsToDeleteModal,
		setOpenSelectedItemsToDeleteModal,
		showScrollFilters,
		setShowScrollFilters,
		openDrawerAddApiToken,
		setOpenDrawerAddApiToken,
		openDrawer,
		setOpenDrawer,
		handleOrganizationFilter,
		handleScroll,
		handleSortParams,
		containerRef,
		handleAddItem,
		handleDeleteMultipleItems,
		handleReset,
		ref,
		widthContainer,
		isOverflowFilters,
		handleViewMore,
		handleRowClicked,
		handleSortStatusChange,
		measurabilitiesQueryParams,
		handleProducerFilter,
		handleCountryFilter,
		handleTechnologyFilter,
		measurabilitiesSortStatus,
		measurabilitiesSortParams,
		setMeasurabilitiesOpenConfirmationModalDelete,
		measurabilitiesOpenConfirmationModalDelete,
		measurabilitiesSelectedRow,
		setAddMeasurabilityProjectFormOpened,
		addMeasurabilityProjectFormOpened,
		setUpdateMeasurabilityProjectFormOpened,
		updateMeasurabilityProjectFormOpened,
		setMeasurabilitiesDatatableOptions,
		measurabilitiesDatatableOptions,
		setMeasurabilitiesSelectedRow,
		handleActiveFilter,
		queryParamsOrganizations,
		resetFilterProducers,
		setResetFilterProducers,
	};
};
